/**
 * Dynamic Input Bridge (in-canvas side).
 *
 * Mounted inside the R3F scene (SceneContent) of every workbench viewport.
 * Its only jobs:
 *   - bind the live camera / canvas / invalidate into the shared
 *     dynamic-input engine (single source for the cursor tooltip and the
 *     synthetic point injection),
 *   - keep the cursor ground-plane position fresh on pointer moves,
 *   - track real left-clicks as the new relative-input base point.
 *
 * It renders nothing and stays neutral across workbenches: without a
 * mounted DynamicInputProvider (captureActive=false) all handlers bail out
 * on the first check.
 */

import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import {
    bindDynamicEngineRefs,
    onCanvasClick,
    updateCursorGroundFromClient,
} from '../core/dynamicInput/dynamicInputEngine';
import { isDynamicInputActive } from '../state/useDynamicInputStore';

export function DynamicInputBridge() {
    const camera = useThree((s) => s.camera);
    const gl = useThree((s) => s.gl);
    const invalidate = useThree((s) => s.invalidate);

    // Engine binding: always current with the viewport that mounted last.
    useEffect(() => {
        bindDynamicEngineRefs({ camera, canvas: gl.domElement, invalidate });
        return () => {
            bindDynamicEngineRefs({ camera: null, canvas: null, invalidate: null });
        };
    }, [camera, gl, invalidate]);

    // Live cursor ground + real-click base point tracking.
    useEffect(() => {
        const canvas = gl.domElement;
        const handleMove = (e: PointerEvent) => {
            if (!isDynamicInputActive()) return;
            if (e.pointerId === 991) return; // synthetic dynamic point must not move the readout
            updateCursorGroundFromClient(e.clientX, e.clientY);
        };
        const handleDown = (e: PointerEvent) => {
            if (!isDynamicInputActive()) return;
            if (e.pointerId === 991) return; // synthesized dynamic point commits its own anchor
            // Clicking back into the viewport returns keyboard ownership to
            // Dynamic Input (the bottom command line grabs focus when clicked).
            const focused = document.activeElement as HTMLElement | null;
            if (focused && (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA')) {
                focused.blur();
            }
            if (e.button === 0) onCanvasClick(e.clientX, e.clientY);
        };
        canvas.addEventListener('pointermove', handleMove);
        canvas.addEventListener('pointerdown', handleDown);
        return () => {
            canvas.removeEventListener('pointermove', handleMove);
            canvas.removeEventListener('pointerdown', handleDown);
        };
    }, [gl]);

    return null;
}
